import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Compass01 } from '@untitledui/icons';
import { matchApi } from '@/entities/match';
import type { Match } from '@/entities/match';
import { useActiveEvent } from '@/entities/event';
import { MatchCardSkeleton } from '@/features/matches/ui/MatchCardSkeleton';
import { MatchDetailsDrawer } from '@/features/matches/ui/MatchDetailsDrawer';
import { QUERY_KEYS, ROUTES } from '@/shared/config/constants';
import { cn } from '@/shared/lib/cn';
import { EmptyState, MatchCard, SearchInput } from '@/shared/ui';

const FILTERS = [
  { id: 'todos', label: 'Todos' },
  { id: 'alto', label: 'Alto Potencial' },
] as const;
type FilterId = (typeof FILTERS)[number]['id'];

export const MatchesPage = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [filter, setFilter] = useState<FilterId>('todos');
  const [search, setSearch] = useState('');
  const [openMatch, setOpenMatch] = useState<Match | null>(null);
  const { eventoId, isLoading: isLoadingEvent, isError: isErrorEvent } = useActiveEvent();

  const { data, isLoading: isLoadingMatches, isError: isErrorMatches, refetch, isRefetching } = useQuery({
    queryKey: [...QUERY_KEYS.MATCHES, eventoId] as const,
    queryFn: () => matchApi.list(eventoId ?? ''),
    enabled: !!eventoId,
    staleTime: 5 * 60 * 1000,
  });

  const isLoading = isLoadingEvent || (!!eventoId && isLoadingMatches);
  const isError = isErrorEvent || isErrorMatches;

  const acceptMutation = useMutation({
    mutationFn: matchApi.accept,
    onSuccess: ({ match }) => {
      toast.success(`Vamos conversar com ${match.participante.nome}!`);
      void qc.invalidateQueries({ queryKey: QUERY_KEYS.MATCHES });
      void qc.invalidateQueries({ queryKey: QUERY_KEYS.CONVERSATIONS });
      setOpenMatch(null);
      navigate(ROUTES.APP.CHAT_CONVERSATION(match.id));
    },
    onError: () => toast.error('Não consegui aceitar. Tente novamente.'),
  });

  const skipMutation = useMutation({
    mutationFn: matchApi.skip,
    onSuccess: () => {
      toast('Match pulado');
      void qc.invalidateQueries({ queryKey: QUERY_KEYS.MATCHES });
      setOpenMatch(null);
    },
    onError: () => toast.error('Não consegui pular. Tente novamente.'),
  });

  const matches = useMemo<Match[]>(() => data?.matches ?? [], [data]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return matches.filter((m) => {
      if (m.status === 'PULADO') return false;
      if (filter === 'alto' && !m.altoPotencial) return false;
      if (q) {
        const text = `${m.participante.nome} ${m.participante.empresa ?? ''}`.toLowerCase();
        if (!text.includes(q)) return false;
      }
      return true;
    });
  }, [matches, filter, search]);

  const totalMatches = matches.filter((m) => m.status !== 'PULADO').length;
  const isFiltering = filter !== 'todos' || search.trim().length > 0;

  return (
    <section className="mx-auto flex w-full max-w-3xl flex-col gap-6">
      <header className="flex flex-col gap-1.5">
        <h1 className="text-3xl font-semibold tracking-tight text-gray-900">Matches</h1>
        <p className="text-sm text-gray-500">
          {isLoading
            ? 'Carregando suas conexões…'
            : totalMatches === 0
              ? 'Estamos buscando suas conexões. Volte em alguns minutos.'
              : `${totalMatches} ${totalMatches === 1 ? 'conexão recomendada' : 'conexões recomendadas'} pelo algoritmo.`}
        </p>
      </header>

      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="flex-1">
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Buscar por nome ou empresa"
              ariaLabel="Buscar matches"
            />
          </div>

          <div
            className="flex shrink-0 gap-1.5 overflow-x-auto"
            role="group"
            aria-label="Filtros"
          >
            {FILTERS.map((f) => {
              const active = filter === f.id;
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setFilter(f.id)}
                  aria-pressed={active}
                  className={cn(
                    'inline-flex h-10 shrink-0 items-center rounded-full border px-4 text-sm font-semibold transition-colors',
                    active
                      ? 'border-black bg-black text-white'
                      : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50',
                  )}
                >
                  {f.label}
                </button>
              );
            })}
          </div>
        </div>

        {!isLoading && filtered.length > 0 ? (
          <p className="text-xs text-gray-500">
            {filtered.length} {filtered.length === 1 ? 'resultado' : 'resultados'}
            {isFiltering ? ' com os filtros atuais' : ''}
          </p>
        ) : null}
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-3" aria-busy="true">
          {Array.from({ length: 5 }).map((_, i) => (
            <MatchCardSkeleton key={i} />
          ))}
        </div>
      ) : isError ? (
        <EmptyState
          icon={Compass01}
          title="Não conseguimos carregar seus matches"
          description="Algo deu errado por aqui. Tente recarregar a lista."
          action={
            <button
              type="button"
              onClick={() => void refetch()}
              disabled={isRefetching}
              className="inline-flex h-10 items-center rounded-full bg-black px-4 text-sm font-semibold text-white transition hover:bg-gray-900 disabled:opacity-60"
            >
              {isRefetching ? 'Recarregando…' : 'Tentar novamente'}
            </button>
          }
        />
      ) : totalMatches === 0 ? (
        <EmptyState
          icon={Compass01}
          title="Sem matches por enquanto"
          description="Estamos buscando suas conexões. Volte em alguns minutos. Novos matches surgem durante o evento."
        />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Compass01}
          title="Nenhum resultado com esses filtros"
          description="Tente limpar os filtros ou ajustar a busca."
          action={
            <button
              type="button"
              onClick={() => {
                setFilter('todos');
                setSearch('');
              }}
              className="inline-flex h-10 items-center rounded-full bg-black px-4 text-sm font-semibold text-white transition hover:bg-gray-900"
            >
              Limpar filtros
            </button>
          }
        />
      ) : (
        <div className="flex flex-col gap-3">
          {filtered.map((match) => (
            <MatchCard
              key={match.id}
              match={match}
              onOpen={() => setOpenMatch(match)}
              onAccept={() => acceptMutation.mutate(match.id)}
              onSkip={() => skipMutation.mutate(match.id)}
              isAccepting={acceptMutation.isPending && acceptMutation.variables === match.id}
            />
          ))}
        </div>
      )}

      <MatchDetailsDrawer
        match={openMatch}
        onClose={() => setOpenMatch(null)}
        onAccept={(id) => acceptMutation.mutate(id)}
        onSkip={(id) => skipMutation.mutate(id)}
        isAccepting={acceptMutation.isPending}
      />
    </section>
  );
};
